import { z } from 'zod';
import { ThemeIdSchema } from '../catalog/platformCatalogIds';
import { optimizedMediaUrlSchema } from '../content/studio';
import { ProjectLocalePolicySchema } from '../locales/platformLocales';
import {
  CustomColorSetsSchema,
  ProjectColorSetIdSchema,
} from './customColorSet';
import {
  CORNER_ENUM_KEY_VALUES,
  SPACING_ENUM_KEY_VALUES,
  TYPOGRAPHY_ENUM_KEY_VALUES,
} from './types';
import { OPEN_GRAPH_IMAGE_MODE } from './openGraphImage';

export const ProjectThemeConfigSchema = z.object({
  themeId: ThemeIdSchema,
  colorsetId: ProjectColorSetIdSchema,
  spacingEnum: z.enum(SPACING_ENUM_KEY_VALUES),
  cornerEnum: z.enum(CORNER_ENUM_KEY_VALUES).optional(),
  typographyEnum: z.enum(TYPOGRAPHY_ENUM_KEY_VALUES).optional(),
  customColorSets: CustomColorSetsSchema.optional(),
});

export type ProjectThemeConfig = z.infer<typeof ProjectThemeConfigSchema>;

const localeKey = z.string().regex(/^[a-z]{2}(-[a-z0-9]{2,8})*$/);

const openGraphImageValueSchema = z.union([optimizedMediaUrlSchema, z.literal('')]);

export const LocalizedSeoSchema = z.object({
  title: z.record(localeKey, z.string().min(10).max(60)),
  description: z.record(localeKey, z.string().min(50).max(160)),
  /** Custom social preview when `openGraphImageMode` is `custom`; ignored in auto. */
  openGraphImage: openGraphImageValueSchema.default(''),
  openGraphImageMode: z
    .enum([OPEN_GRAPH_IMAGE_MODE.auto, OPEN_GRAPH_IMAGE_MODE.custom])
    .default(OPEN_GRAPH_IMAGE_MODE.auto),
  sitemapIndexPriority: z.number().min(0).max(1).default(0.5),
});

export type LocalizedSeo = z.infer<typeof LocalizedSeoSchema>;

export const SEO_TITLE_MIN = 10;
export const SEO_TITLE_MAX = 60;
export const SEO_DESCRIPTION_MIN = 50;
export const SEO_DESCRIPTION_MAX = 160;

const SEO_TITLE_PAD = ' | Restaurant site';
const SEO_DESCRIPTION_PAD =
  ' Discover menu details, opening hours, and how to reach us on this website.';

function clampSeoLocaleMap(
  record: Record<string, unknown> | undefined,
  min: number,
  max: number,
  pad: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  const safePad = pad.length > 0 ? pad : '.';
  for (const [locale, raw] of Object.entries(record ?? {})) {
    let value = typeof raw === 'string' ? raw.trim() : '';
    if (value.length > max) {
      value = value.slice(0, max);
    }
    if (value.length < min) {
      const filler = safePad.repeat(Math.ceil((min - value.length) / safePad.length));
      value = `${value}${filler}`.slice(0, max);
    }
    if (value.length < min) {
      value = value.padEnd(min, '.');
    }
    out[locale] = value.slice(0, max);
  }
  return out;
}

function coerceOpenGraphImage(raw: unknown): string {
  if (typeof raw !== 'string') {
    return '';
  }
  const trimmed = raw.trim();
  if (!trimmed || /cdn\.web-studio\.example/i.test(trimmed)) {
    return '';
  }
  const parsed = openGraphImageValueSchema.safeParse(trimmed);
  return parsed.success ? parsed.data : '';
}

function coerceOpenGraphImageMode(raw: unknown): LocalizedSeo['openGraphImageMode'] {
  if (raw === OPEN_GRAPH_IMAGE_MODE.custom) {
    return OPEN_GRAPH_IMAGE_MODE.custom;
  }
  return OPEN_GRAPH_IMAGE_MODE.auto;
}

/**
 * Repairs SEO maps that fail strict min/max length (e.g. short bootstrap names)
 * so draft load / publish can proceed without crashing.
 */
export function coerceLocalizedSeo(input: unknown): LocalizedSeo {
  const base =
    input && typeof input === 'object' ? (input as Partial<LocalizedSeo> & Record<string, unknown>) : {};
  const priority =
    typeof base.sitemapIndexPriority === 'number' && Number.isFinite(base.sitemapIndexPriority)
      ? Math.min(1, Math.max(0, base.sitemapIndexPriority))
      : 0.5;
  const openGraphImage = coerceOpenGraphImage(base.openGraphImage);
  let mode = coerceOpenGraphImageMode(base.openGraphImageMode);
  /** Legacy rows with only a real image URL and no mode → treat as custom. */
  if (base.openGraphImageMode == null && openGraphImage) {
    mode = OPEN_GRAPH_IMAGE_MODE.custom;
  }
  if (mode === OPEN_GRAPH_IMAGE_MODE.custom && !openGraphImage) {
    mode = OPEN_GRAPH_IMAGE_MODE.auto;
  }

  return LocalizedSeoSchema.parse({
    title: clampSeoLocaleMap(
      base.title as Record<string, unknown> | undefined,
      SEO_TITLE_MIN,
      SEO_TITLE_MAX,
      SEO_TITLE_PAD,
    ),
    description: clampSeoLocaleMap(
      base.description as Record<string, unknown> | undefined,
      SEO_DESCRIPTION_MIN,
      SEO_DESCRIPTION_MAX,
      SEO_DESCRIPTION_PAD,
    ),
    openGraphImage: mode === OPEN_GRAPH_IMAGE_MODE.custom ? openGraphImage : '',
    openGraphImageMode: mode,
    sitemapIndexPriority: priority,
  });
}

export const ProjectConfigurationSchema = z
  .object({
    vertical: z.string().min(1),
    packageTier: z.string().min(1),
    /** Single-page (minimum tier) vs multi-page site topology. */
    siteMode: z.enum(['minimal', 'standard']).optional(),
    themeId: ThemeIdSchema,
    colorsetId: ProjectColorSetIdSchema,
    spacingEnum: z.enum(SPACING_ENUM_KEY_VALUES),
    cornerEnum: z.enum(CORNER_ENUM_KEY_VALUES).optional(),
    typographyEnum: z.enum(TYPOGRAPHY_ENUM_KEY_VALUES).optional(),
    /** Project-owned guided custom palettes (Site Appearance). */
    customColorSets: CustomColorSetsSchema.optional(),
    businessName: z.string().optional(),
    addons: z
      .object({
        payments: z.boolean().optional(),
        crm: z.boolean().optional(),
        onlineReservation: z.boolean().optional(),
      })
      .optional(),
  })
  .merge(ProjectLocalePolicySchema);

export type ProjectConfiguration = z.infer<typeof ProjectConfigurationSchema>;
